// Revision number (alphanumeric) length
import { Delta, Line, TextDocument } from '@typewriter/document';
import { applyBitmask, combineBitmasks } from '@typewriter/json-patch';
const MAX = 900_000;
export const REV_LENGTH = 5;
export const EMPTY_REV = '0'.repeat(REV_LENGTH);
export const EMPTY_OBJECT = Object.freeze({});
export const EMPTY_ARRAY = Object.freeze([]);
/** Break patches into smaller chunks if they are larger than 0.9MB */
export function breakPatchIntoSmallerChanges(ops) {
    // Most common case is that the patch is small enough to send in one go
    if (getJSONByteSize(ops) < MAX)
        return [ops];
    // Otherwise, break it up into 0.9MB chunks
    const chunks = [];
    let chunk = [];
    let total = 0;
    for (const op of ops) {
        const opSize = getJSONByteSize(op);
        if (total + opSize > MAX) {
            chunks.push(chunk);
            chunk = [];
            total = 0;
        }
        // If a single op is bigger than 0.9MB, break it up into smaller ops
        if (opSize > MAX) {
            throw new Error('Cannot break up op ' + op.op + ', too big, > 0.9MB');
        }
        chunk.push(op);
        total += opSize;
    }
    if (chunk.length > 0) {
        chunks.push(chunk);
    }
    return chunks;
}
/** Optimize the changes being sent to the server by collapsing them into a single change. */
export function collapseLWWChanges(changes) {
    const lastOp = new Map();
    changes
        .map(c => c.patch)
        .flat()
        .forEach(op => {
        if (op.op === '@inc') {
            const prev = lastOp.get(op.path);
            if (prev && typeof prev.value === 'number') {
                op = { ...op, value: prev.value + op.value };
            }
        }
        else if (op.op === '@bit') {
            const prev = lastOp.get(op.path);
            if (prev && prev.op === '@bit') {
                op = { ...op, value: combineBitmasks(prev.value, op.value) };
            }
            else if (prev && typeof prev.value === 'number') {
                op = { ...op, value: applyBitmask(prev.value, op.value) };
            }
        }
        lastOp.set(op.path, op);
    });
    return { rev: changes[0].rev, patch: Array.from(lastOp.values()) };
}
function getJSONByteSize(data) {
    return JSON.stringify(data).length;
}
const cachedPathExpr = new Map();
export function pathExistsIn(path, prefixes) {
    if (!prefixes.length)
        return false;
    // Support wildcard such as '/docs/*/title'
    const key = prefixes.sort().join(',');
    let expr = cachedPathExpr.get(key);
    if (!expr) {
        expr = getPathExpr(prefixes);
        cachedPathExpr.set(key, expr);
    }
    return expr.test(path);
}
function getPathExpr(paths) {
    return new RegExp(paths.map(prop => `^${prop.replace(/\*/g, '[^\\/]*')}(/.*)?$`).join('|'));
}
export function debounce(func, wait) {
    let timeoutId;
    let lastInvocationTime = 0;
    return function (...args) {
        const now = Date.now();
        const elapsed = now - lastInvocationTime;
        const later = () => {
            lastInvocationTime = Date.now();
            timeoutId = undefined;
            func.apply(this, args);
        };
        if (elapsed > wait) {
            clearTimeout(timeoutId);
            later();
        }
        else if (!timeoutId) {
            timeoutId = setTimeout(later, wait - elapsed);
        }
    };
}
export const textConversion = {
    revive: {
        Delta: (obj) => convertJSON(obj, (value) => {
            if (value && typeof value === 'object' && Array.isArray(value.ops)) {
                return new Delta(value.ops);
            }
            return value;
        }),
        TextDocument: (obj) => convertJSON(obj, (value) => {
            if (value && typeof value === 'object') {
                if (Array.isArray(value.ops)) {
                    return new TextDocument(new Delta(value.ops));
                }
                else if (Array.isArray(value.lines) && value.byId && typeof value.length === 'number') {
                    return new TextDocument(value.lines.map((l) => Line.create(new Delta(l.content.ops), l.attributes, l.id)));
                }
            }
            return value;
        }),
    },
    replace: {
        Delta: (obj) => convertJSON(obj, (value) => {
            if (value instanceof Delta) {
                return { ops: value.ops };
            }
            return value;
        }),
        TextDocument: (obj) => convertJSON(obj, (value) => {
            if (value instanceof TextDocument ||
                (value &&
                    Array.isArray(value.lines) &&
                    value.byId &&
                    typeof value.length === 'number' &&
                    typeof value.toDelta === 'function')) {
                return { ops: value.toDelta().ops };
            }
            return value;
        }),
    },
};
function convertJSON(obj, converter) {
    const root = { '': obj };
    function revive(obj) {
        const orig = obj;
        for (const key in obj) {
            let value = converter(obj[key]);
            if (value === obj[key] && value && typeof value === 'object') {
                value = revive(value);
            }
            if (value !== obj[key]) {
                if (obj === orig)
                    obj = shallowCopy(obj);
                obj[key] = value;
            }
        }
        return obj;
    }
    return revive(root)[''];
}
function shallowCopy(obj) {
    if (!obj || typeof obj !== 'object')
        return obj;
    if (Array.isArray(obj))
        return [...obj];
    return { ...obj };
}
